import { createScheduleComponent } from '@play-co/components';
import { Match2Reward } from '../match2/rewards/match2-rewards.ruleset';
import { duration } from '../utils/duration';

/**
 * Buddies Event Schedule V2
 * 7-day events (Monday 00:00 to next Monday 00:00, exclusive) on a bi-weekly cadence.
 * Events start on Monday of the week after new levels are released on Tuesday.
 * Bi-weekly is implemented using explicit `once` dates since createScheduleComponent
 * does not support a biweekly pattern natively.
 */
export const buddiesSchedule = createScheduleComponent(
  '2026-02-08',
  'Asia/Tokyo',
  {
    once: [
      { at: '2026-02-09', duration: { days: 7 } },
      { at: '2026-02-23', duration: { days: 7 } },
      { at: '2026-03-09', duration: { days: 7 } },
      { at: '2026-03-23', duration: { days: 7 } },
      { at: '2026-04-06', duration: { days: 7 } },
      { at: '2026-04-20', duration: { days: 7 } },
      { at: '2026-05-04', duration: { days: 7 } },
      // Resume bi-weekly cadence after the Boost Rocket V2 experiment.
      { at: '2026-06-01', duration: { days: 7 } },
      { at: '2026-06-15', duration: { days: 7 } },
      { at: '2026-06-29', duration: { days: 7 } },
      { at: '2026-07-13', duration: { days: 7 } },
      { at: '2026-07-27', duration: { days: 7 } },
      { at: '2026-08-10', duration: { days: 7 } },
      { at: '2026-08-24', duration: { days: 7 } },
      { at: '2026-09-07', duration: { days: 7 } },
      { at: '2026-09-21', duration: { days: 7 } },
      { at: '2026-10-05', duration: { days: 7 } },
      { at: '2026-10-19', duration: { days: 7 } },
      { at: '2026-11-02', duration: { days: 7 } },
      { at: '2026-11-16', duration: { days: 7 } },
      { at: '2026-11-30', duration: { days: 7 } },
      { at: '2026-12-14', duration: { days: 7 } },
      { at: '2026-12-28', duration: { days: 7 } },
    ],
  },
);

/** Prefix for dummy buddy player IDs (used in debug cheats) */
export const buddiesDummyPrefix = 'Udummy-';

/** Level at which the buddies feature is unlocked */
export const buddiesUnlockLevel = 20;

/** Maximum number of friends a player can have in an event */
export const buddiesMaxParty = 4;

/** Duration of a buddies event, they should be deleted after this time */
export const buddiesEventExpirationTime = duration({ days: 12 });

export const buddiesStatus = [
  'active',
  'inviteSent',
  'inviteReceived',
] as const;

export type BuddiesStatus = (typeof buddiesStatus)[number];
/** V2 milestone score thresholds (party total score) */
export const buddiesMilestoneThresholds = [20, 50, 150, 300] as const;

/** V2 base gem rewards per milestone (final milestone only, not cumulative) */
export const buddiesMilestoneBaseGems = [50, 100, 200, 500] as const;

/**
 * Reward multiplier by number of eligible party members.
 * Index: eligible member count (0-4), value: multiplier.
 * A member is eligible if their personal score >= buddiesEligibilityThreshold.
 */
export const buddiesEligibilityMultipliers = [1.0, 1.0, 1.2, 1.5, 2.0] as const;

/** Minimum personal score to qualify for end-of-event rewards */
export const buddiesEligibilityThreshold = 20;

/** Minimum contribution to qualify for rewards (same as eligibility threshold) */
export const buddiesMinimumContribution = 20;

/** Nudge cooldown: max once per recipient per 24 hours */
export const buddiesNudgeRecipientCooldown = duration({ hours: 24 });

/** Max nudges a player can send per event */
export const buddiesNudgeMaxPerEvent = 2;

/** Stop showing a player as a suggestion when they have this many pending invites */
export const buddiesMaxPendingInvitesForSuggestions = 10;

/** Number of oldest pending invites to display to the user */
export const buddiesMaxDisplayedInvites = 5;

/** Enable or disable friends suggestions in the feature */
export const buddiesSuggestionsEnabled = false;

/** Number of suggested players to show */
export const buddiesMaxSuggestedPlayers = 5;

/** Rewards granted to the party owner when a new member joins */
export const buddiesPartyJoinRewards: Match2Reward[] = [
  { type: 'powerups', subType: 'rocket', amount: 1 },
  { type: 'powerups', subType: 'bomb', amount: 1 },
];
